import React, { useState } from 'react';
import { Check, Globe2, MailCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { persistAuthLanguage, readAuthLanguage } from '../auth/authLanguage';
import brandMark from '../assets/brand-assets/buoy-mark.svg';
import brandWatermark from '../assets/brand-assets/buoy-watermark-white.png';
import './Login.css';
import './SignUp.css';

const PAGE_COPY = {
  en: {
    dir: 'ltr',
    languageButton: 'عربي',
    languageButtonLabel: 'Switch to Arabic',
    brandLines: ['Disaster', 'Management', 'Smart System'],
    headline: ['Join the readiness', 'and response team.'],
    subline: 'One account connects you to the command center, the SOS field app, and every drill and task in your organization.',
    perks: [
      'Instant incident and task alerts',
      'Duty and readiness tied to your location',
      'Web and SOS app access',
    ],
    benefitsLabel: 'Account benefits',
    title: 'Join DisasterMS by invitation',
    alreadyAccount: 'Already have an account?',
    login: 'Login',
    invitationHeading: 'Your institution creates your access',
    invitationCopy: 'Ask your Institution Administrator to send you a secure invitation from Users & Access. The invitation automatically connects you to the correct institution and departments.',
    steps: [
      'Your administrator chooses your departments and app access.',
      'Open the one-time invitation link they send you.',
      'Complete your account, then wait for administrator activation.',
    ],
    haveToken: 'I have an invitation token',
    backToLogin: 'Back to login',
    privacyNote: 'Institutions and department lists are never exposed through public registration.',
    allSystems: 'ALL SYSTEMS OPERATIONAL',
  },
  ar: {
    dir: 'rtl',
    languageButton: 'English',
    languageButtonLabel: 'Switch to English',
    brandLines: ['نظام إدارة', 'الأزمات الذكي'],
    headline: ['انضم إلى فريق', 'الجاهزية والاستجابة.'],
    subline: 'حساب واحد يربطك بمركز القيادة، والتطبيق الميداني SOS، وكل تمارين ومهام مؤسستك.',
    perks: [
      'تنبيهات فورية للحوادث والمهام',
      'جاهزية ومناوبات مرتبطة بموقعك',
      'وصول عبر الويب وتطبيق SOS',
    ],
    benefitsLabel: 'مزايا الحساب',
    title: 'انضم إلى DisasterMS عن طريق دعوة',
    alreadyAccount: 'لديك حساب بالفعل؟',
    login: 'تسجيل الدخول',
    invitationHeading: 'مؤسستك هي التي تنشئ صلاحية وصولك',
    invitationCopy: 'اطلب من مسؤول المؤسسة إرسال دعوة آمنة لك من صفحة المستخدمين والصلاحيات. تربطك الدعوة تلقائيًا بالمؤسسة والأقسام الصحيحة.',
    steps: [
      'يحدد المسؤول أقسامك وصلاحية استخدام التطبيقات.',
      'افتح رابط الدعوة المرسل لك والمخصص للاستخدام مرة واحدة.',
      'أكمل حسابك، ثم انتظر تفعيل المسؤول.',
    ],
    haveToken: 'لدي رمز دعوة',
    backToLogin: 'العودة لتسجيل الدخول',
    privacyNote: 'لا يتم عرض المؤسسات أو قوائم الأقسام في التسجيل العام.',
    allSystems: 'جميع الأنظمة تعمل',
  },
};

const SignUp = () => {
  const [language, setLanguage] = useState(readAuthLanguage);
  const text = PAGE_COPY[language];

  const toggleLanguage = () => {
    const nextLanguage = language === 'en' ? 'ar' : 'en';
    persistAuthLanguage(nextLanguage);
    setLanguage(nextLanguage);
  };

  return (
    <div
      className="dms-login-page dms-register-page"
      dir={text.dir}
      lang={language}
      data-testid="disasterms-signup-page"
    >
      <div className="dms-login-top-line" aria-hidden="true" />
      <div className="dms-login-glow" aria-hidden="true" />
      <div className="dms-login-decor" aria-hidden="true">
        <img className="dms-login-watermark" src={brandWatermark} alt="" draggable="false" />
      </div>

      <button
        type="button"
        className="dms-language-toggle"
        onClick={toggleLanguage}
        aria-label={text.languageButtonLabel}
      >
        <Globe2 size={15} aria-hidden="true" />
        <span>{text.languageButton}</span>
      </button>

      <main className="dms-login-main dms-register-main">
        <section className="dms-brand-story dms-register-story" aria-labelledby="disasterms-signup-headline">
          <div className="dms-brand-lockup">
            <img className="dms-brand-mark" src={brandMark} alt="" draggable="false" />
            <p className="dms-brand-wordmark">
              {text.brandLines.map((line) => (
                <React.Fragment key={line}>{line}<br /></React.Fragment>
              ))}
            </p>
          </div>
          <h1 id="disasterms-signup-headline" className="dms-brand-headline dms-register-headline">
            {text.headline.map((line) => <span key={line}>{line}</span>)}
          </h1>
          <p className="dms-register-subline">{text.subline}</p>
          <ul className="dms-register-perks" aria-label={text.benefitsLabel}>
            {text.perks.map((perk) => (
              <li key={perk}>
                <span className="dms-register-perk-icon" aria-hidden="true"><Check size={13} strokeWidth={3} /></span>
                <span>{perk}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="dms-auth-card dms-register-card dms-invitation-only-card" aria-labelledby="signup-card-title">
          <h2 id="signup-card-title" className="dms-auth-card-title">{text.title}</h2>
          <p className="dms-auth-card-copy">{text.alreadyAccount} <Link to="/login">{text.login}</Link></p>

          <div className="dms-invitation-only-message">
            <MailCheck size={28} aria-hidden="true" />
            <div>
              <h3>{text.invitationHeading}</h3>
              <p>{text.invitationCopy}</p>
            </div>
          </div>

          <ol className="dms-invitation-steps">
            {text.steps.map((step, index) => (
              <li key={step}><span>{index + 1}</span><p>{step}</p></li>
            ))}
          </ol>

          <Link className="dms-auth-submit dms-invitation-primary-link" to="/accept-invitation">
            {text.haveToken}
          </Link>
          <Link className="dms-invitation-secondary-link" to="/login">{text.backToLogin}</Link>
          <p className="dms-invitation-privacy-note">{text.privacyNote}</p>
        </section>
      </main>

      <footer className="dms-login-footer dms-register-footer">
        <span className="dms-login-footer-status">
          <span className="dms-login-footer-dot" aria-hidden="true" />
          <span>{text.allSystems}</span>
        </span>
        <span className="dms-login-footer-spacer" aria-hidden="true" />
        <span className="dms-login-footer-copyright" dir="ltr">© 2026 DisasterMS/SOS Ecosystem</span>
      </footer>
    </div>
  );
};

export default SignUp;
